import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useCallback, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useUser } from "../../user/hooks/useUser"
import type { DynamicDropdown } from "../models/dynamicDropdown"
import {
  DROPDOWN_TYPES,
  DynamicDropdownSchema,
} from "../models/dynamicDropdown"
import type { DrawerMode } from "./DynamicDropdownDrawer"
import DynamicDropdownDrawer from "./DynamicDropdownDrawer"

const typeOptions = DROPDOWN_TYPES.map((t) => ({
  value: String(t.value),
  label: t.label,
}))

export default function DynamicDropdownList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuAdmin ?? false

  const {
    data,
    isLoading,
    page,
    pageSize,
    sort,
    sortDir,
    handleSortChange,
    handlePageChange,
    filters,
    updateFilters,
  } = useDataTable<DynamicDropdown>({
    queryKey: "dynamic-dropdowns",
    url: "dynamic-dropdown",
    schema: DynamicDropdownSchema,
    defaultSort: { field: "type", dir: "asc" },
    defaultPageSize: 50,
  })

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")

  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => {
    setDrawerMode("edit")
  }, [])

  const handleRowClick = useCallback(
    (v: DynamicDropdown) => {
      if (selectedId === v.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(v.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedId, drawerOpened],
  )

  const columns: Column<DynamicDropdown>[] = [
    { key: "id", label: "ID", sortable: true, width: 60 },
    {
      key: "type",
      label: t("dynamicDropdowns.type"),
      sortable: true,
      render: (v) => (
        <Badge color="blue" variant="light" size="sm">
          {v.type_name}
        </Badge>
      ),
    },
    {
      key: "option",
      label: t("dynamicDropdowns.option"),
      sortable: true,
      render: (v) => <Text size="sm">{v.option}</Text>,
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("dynamicDropdowns.title")}
          actions={
            canManage ? (
              <Button
                onClick={() => openDrawer(null, "create")}
                leftSection={<i className="fa fa-plus" />}
              >
                <Trans i18nKey="dynamicDropdowns.create" />
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md" mb="md">
        <Group grow align="flex-end">
          <TextInput
            label={t("dynamicDropdowns.option")}
            placeholder={t("dynamicDropdowns.option")}
            value={(filters.option as string) ?? ""}
            onChange={(e) =>
              updateFilters({ option: e.currentTarget.value || undefined })
            }
          />
          <Select
            label={t("dynamicDropdowns.type")}
            placeholder={t("dynamicDropdowns.allTypes")}
            data={typeOptions}
            clearable
            value={(filters.type as string | undefined) ?? null}
            onChange={(val) => updateFilters({ type: val || undefined })}
          />
        </Group>
      </Card>
      <DataTable<DynamicDropdown>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("dynamicDropdowns.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={(e) => {
                    e.stopPropagation()
                    openDrawer(v.id, "edit")
                  }}
                >
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />
      <DynamicDropdownDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedId={selectedId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
